/**
 * Beat Shop API client — same auth conventions as services/api/client.ts
 * (Firebase Bearer token + X-Artist-Id), pointed at the beatshop function.
 */
import { getAuthToken } from '../../utils/getAuthToken';
import {
  Beat, BeatDetail, CatalogStats, CheckoutResult, LicenseVersion,
  SellerBeat, SellerMeta, SellerSale, UploadSlot, VaultItem,
} from './types';

const BASE_URL =
  process.env.REACT_APP_BEATSHOP_API_BASE_URL ||
  `${process.env.REACT_APP_API_BASE_URL || ''}/beatshop`;

const V1 = `${BASE_URL}/v1`;

async function beatshopFetch(path: string, options: RequestInit = {}): Promise<any> {
  const token = await getAuthToken();
  const currentArtistId = localStorage.getItem('currentArtistId');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(options.headers as Record<string, string> | undefined),
  };
  if (currentArtistId) headers['X-Artist-Id'] = currentArtistId;

  const res = await fetch(`${V1}${path}`, { ...options, headers });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const error = new Error(data?.error?.message || 'Beat Shop request failed');
    (error as any).status = res.status;
    (error as any).code = data?.error?.code;
    throw error;
  }
  return data;
}

// ── catalog / buyer ──
export const getCatalog = (params: Record<string, string>): Promise<{ beats: Beat[]; total: number; limit: number; offset: number }> =>
  beatshopFetch(`/catalog?${new URLSearchParams(params).toString()}`);
export const getStats = (): Promise<CatalogStats> => beatshopFetch('/catalog/stats');
export const getBeat = (beatId: string): Promise<BeatDetail> => beatshopFetch(`/catalog/${beatId}`);
export const setFavorite = (beatId: string, favorited: boolean): Promise<{ favorited: boolean; favorite_count: number }> =>
  beatshopFetch(`/catalog/${beatId}/favorite`, { method: favorited ? 'PUT' : 'DELETE' });
export const getCurrentLicense = (kind: 'buyer' | 'seller' = 'buyer'): Promise<LicenseVersion> =>
  beatshopFetch(`/license/current?kind=${kind}`);

export const checkout = (body: {
  beat_id: string; tender: string; license_version_id: string; agreed: true;
}, idempotencyKey: string): Promise<CheckoutResult> =>
  beatshopFetch('/checkout', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });

export const getOrder = (orderId: string): Promise<any> => beatshopFetch(`/orders/${orderId}`);
/** Abandon a pending checkout and release the beat reservation. */
export const cancelPendingOrder = (orderId: string): Promise<{ orderId: string; cancelled: boolean; status: string }> =>
  beatshopFetch(`/orders/${orderId}/cancel`, { method: 'POST' });
export const getVault = (): Promise<{ items: VaultItem[] }> => beatshopFetch('/vault');
export const getVaultItem = (entitlementId: string): Promise<VaultItem> =>
  beatshopFetch(`/vault/${entitlementId}`);
export const mintDownload = (body: {
  entitlement_id: string; file_kind: 'full_mix' | 'stems_zip' | 'stem'; file_label?: string; format?: 'wav' | 'mp3';
}): Promise<{ token: string; token_url: string; expires_in: number }> =>
  beatshopFetch('/downloads', { method: 'POST', body: JSON.stringify(body) });

/** Resolve mint token_url against the Beat Shop API host (never the SPA origin). */
export const resolveDownloadRedeemUrl = (tokenOrUrl: string): string => {
  if (/^https?:\/\//i.test(tokenOrUrl)) {
    try {
      const u = new URL(tokenOrUrl);
      // Relative-looking paths wrongly hosted on the SPA still need the API host
      if (u.pathname.includes('/downloads/')) {
        const token = u.pathname.split('/downloads/').pop() || '';
        return `${V1}/downloads/${token}?format=json`;
      }
    } catch { /* fall through */ }
    return tokenOrUrl.includes('?') ? `${tokenOrUrl}&format=json` : `${tokenOrUrl}?format=json`;
  }
  const token = tokenOrUrl.replace(/^\/+/, '').split('/').pop() || tokenOrUrl;
  return `${V1}/downloads/${token}?format=json`;
};

const triggerBrowserDownload = (blob: Blob, filename: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objectUrl);
};

/**
 * Mint + redeem + save file in-place (Excel-export style). Does not navigate the SPA.
 */
export const downloadEntitlementFile = async (body: {
  entitlement_id: string; file_kind: 'full_mix' | 'stems_zip' | 'stem'; file_label?: string; format?: 'wav' | 'mp3';
  filenameHint?: string;
}): Promise<void> => {
  const { filenameHint, ...mintBody } = body;
  const minted = await mintDownload(mintBody);
  const redeemUrl = resolveDownloadRedeemUrl(minted.token || minted.token_url);
  const redeemRes = await fetch(redeemUrl, { headers: { Accept: 'application/json' } });
  const redeemData = await redeemRes.json().catch(() => null);
  if (!redeemRes.ok || !redeemData?.url) {
    throw new Error(redeemData?.error?.message || 'Download link could not be redeemed');
  }
  const filename = redeemData.filename || filenameHint || 'beat-download';
  try {
    const fileRes = await fetch(redeemData.url);
    if (!fileRes.ok) throw new Error(`File fetch failed (${fileRes.status})`);
    const blob = await fileRes.blob();
    triggerBrowserDownload(blob, filename);
  } catch {
    // GCS CORS fallback — open signed URL without leaving the Vault route in-place
    const a = document.createElement('a');
    a.href = redeemData.url;
    a.rel = 'noopener';
    a.target = '_blank';
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

// ── seller ──
/** Whether current user may see/use Sell (Secret Manager SELL_ACCESS_EMAIL_LIST). */
export const getSellAccess = (): Promise<{ allowed: boolean; has_access: boolean }> =>
  beatshopFetch('/seller/access');
export const getSellerMeta = (): Promise<SellerMeta> => beatshopFetch('/seller/meta');
export const acceptSellerTerms = (licenseVersionId: string): Promise<{ accepted: boolean }> =>
  beatshopFetch('/seller/terms-acceptance', {
    method: 'POST', body: JSON.stringify({ license_version_id: licenseVersionId, agreed: true }),
  });
export const createBeat = (body: {
  name: string; bpm: number; musical_key: string; genres: string[];
  price_points: number; price_cents: number; stems: string[];
}): Promise<{ beat: SellerBeat; uploads: UploadSlot[] }> =>
  beatshopFetch('/seller/beats', { method: 'POST', body: JSON.stringify(body) });
export const getSellerBeats = (): Promise<{ beats: SellerBeat[] }> => beatshopFetch('/seller/beats');
export const updateBeat = (beatId: string, patch: {
  name?: string; bpm?: number; musical_key?: string; genres?: string[];
  price_points?: number; price_cents?: number;
}): Promise<SellerBeat> =>
  beatshopFetch(`/seller/beats/${beatId}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const filesComplete = (beatId: string): Promise<{
  status: string; duration_seconds?: number; preview_source?: 'uploaded' | 'generated';
}> =>
  beatshopFetch(`/seller/beats/${beatId}/files-complete`, { method: 'POST' });
export const getUploadUrls = (beatId: string): Promise<{ uploads: UploadSlot[] }> =>
  beatshopFetch(`/seller/beats/${beatId}/upload-urls`, { method: 'POST' });
export const deleteBeat = (beatId: string): Promise<{ deleted: boolean }> =>
  beatshopFetch(`/seller/beats/${beatId}`, { method: 'DELETE' });
export const submitBeat = (beatId: string): Promise<any> =>
  beatshopFetch(`/seller/beats/${beatId}/submit`, { method: 'POST' });
export const withdrawBeat = (beatId: string): Promise<any> =>
  beatshopFetch(`/seller/beats/${beatId}/withdraw`, { method: 'POST' });

/** Create a pending edit for a listed beat (metadata only, or metadata + full audio replace). */
export const createListedEdit = (beatId: string, body: {
  name: string; bpm: number; musical_key: string; genres: string[];
  price_points: number; price_cents: number; stems?: string[]; replace_audio?: boolean;
}): Promise<{ edit: any; uploads: UploadSlot[]; replace_audio: boolean }> =>
  beatshopFetch(`/seller/beats/${beatId}/listed-edit`, { method: 'POST', body: JSON.stringify(body) });

export const listedEditFilesComplete = (beatId: string, editId: string): Promise<{
  status: string; duration_seconds?: number; preview_source?: 'uploaded' | 'generated';
}> =>
  beatshopFetch(`/seller/beats/${beatId}/listed-edit/${editId}/files-complete`, { method: 'POST' });

export const getSellerSales = (): Promise<{ sales: SellerSale[]; totals: { total_net_points: number; total_net_cents: number } }> =>
  beatshopFetch('/seller/sales');
export const getSellerRoyalties = (): Promise<{ royalties: any[] }> => beatshopFetch('/seller/royalties');

/** Direct PUT to a signed upload URL (local adapter or GCS). */
export const uploadFile = async (uploadUrl: string, file: File | Blob, contentType?: string): Promise<void> => {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: contentType ? { 'Content-Type': contentType } : undefined,
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
};

// ── admin ──
export const adminGetReviewQueue = (): Promise<{ queue: any[]; listed_edits: any[] }> =>
  beatshopFetch('/admin/review-queue');
export const adminDecide = (beatId: string, decision: 'approve' | 'reject', reason?: string): Promise<any> =>
  beatshopFetch(`/admin/beats/${beatId}/${decision}`, {
    method: 'POST', body: decision === 'reject' ? JSON.stringify({ reason }) : JSON.stringify({}),
  });
export const adminDecideListedEdit = (editId: string, decision: 'approve' | 'reject', reason?: string): Promise<any> =>
  beatshopFetch(`/admin/listed-edits/${editId}/${decision}`, {
    method: 'POST', body: decision === 'reject' ? JSON.stringify({ reason }) : JSON.stringify({}),
  });
export const adminGetBeats = (
  params: { status?: string; limit?: number; offset?: number } = {},
): Promise<{ beats: any[]; total: number; limit: number; offset: number }> => {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return beatshopFetch(`/admin/beats${qs ? `?${qs}` : ''}`);
};

/** Replace one beat's preview clip. Sent as a raw body — the file IS the payload. */
export const adminReplacePreview = async (
  beatId: string,
  file: File,
): Promise<{ replaced: true; bytes: number; durationSeconds: number; preview_url: string }> => {
  const token = await getAuthToken();
  const res = await fetch(`${V1}/admin/beats/${beatId}/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'audio/mpeg', Authorization: `Bearer ${token}` },
    body: file,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const error = new Error(data?.error?.message || 'Preview upload failed');
    (error as any).status = res.status;
    (error as any).code = data?.error?.code;
    throw error;
  }
  return data;
};
export const adminBeatAction = (beatId: string, action: 'delist' | 'relist' | 'takedown', reason?: string): Promise<any> =>
  beatshopFetch(`/admin/beats/${beatId}/${action}`, { method: 'POST', body: JSON.stringify({ reason: reason || '' }) });
export const adminGetOrders = (
  params: { status?: string; limit?: number; offset?: number } = {},
): Promise<{ orders: any[]; total: number; limit: number; offset: number }> => {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return beatshopFetch(`/admin/orders${qs ? `?${qs}` : ''}`);
};
export const adminReverseOrder = (orderId: string, reason: string, relist: boolean): Promise<any> =>
  beatshopFetch(`/admin/orders/${orderId}/reverse`, { method: 'POST', body: JSON.stringify({ reason, relist }) });
export const adminGetFinance = (): Promise<any> => beatshopFetch('/admin/finance/summary');
export const adminGetReconciliation = (): Promise<any> => beatshopFetch('/admin/finance/reconciliation');
export const adminGetConfig = (): Promise<any> => beatshopFetch('/admin/config');
export const adminPutConfig = (body: Record<string, unknown>): Promise<any> =>
  beatshopFetch('/admin/config', { method: 'PUT', body: JSON.stringify(body) });
export const adminGetAudit = (
  params: { limit?: number; offset?: number } = {},
): Promise<{ audit: any[]; total: number; limit: number; offset: number }> => {
  const q = new URLSearchParams();
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return beatshopFetch(`/admin/audit${qs ? `?${qs}` : ''}`);
};

export const adminGetBeatFavoriters = (
  beatId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<{ users: { userId: number; name: string }[]; total: number; limit: number; offset: number }> => {
  const q = new URLSearchParams();
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  return beatshopFetch(`/admin/beats/${beatId}/favorites${q.toString() ? `?${q.toString()}` : ''}`);
};

/** DEV ONLY: simulate the PSP success webhook for the mock adapter. */
export const devSimulatePspSuccess = async (paymentIntentId: string): Promise<void> => {
  await fetch(`${V1}/hooks/psp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: `evt_dev_${paymentIntentId}`,
      type: 'payment_intent.succeeded',
      data: { object: { id: paymentIntentId } },
    }),
  });
};
export const isMockPayments = (clientSecret: string | null | undefined): boolean =>
  !!clientSecret && clientSecret.startsWith('pi_mock_');
